import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

export const TINY_DESK_PAGE_PATH = "/tiny-desk-ai-video-generator";
export const TINY_DESK_PENDING_STATE_KEY = "pendingTinyDeskState";

/** 固定舞台参考；服务端按这些 key 配原声，见 preset-audio.ts。 */
export const TINY_DESK_MOTION_PATH = "presets/tiny-desk/motion-v2.mp4";
export const TINY_DESK_MOTION_SECONDS = 11;
export const TINY_DESK_MOTION_LITE_PATH =
	"presets/tiny-desk/motion-lite-v2.mp4";
export const TINY_DESK_MOTION_LITE_SECONDS = 2;
export const TINY_DESK_MOTION_ASPECT_RATIO: I2VAspectRatio = "9:16";

/** 默认乐手可以换成朋友；删除用户选的照片后恢复这两张。 */
export const TINY_DESK_KEYBOARD_IMAGE =
	"https://assets.imagesart.ai/home/showcase/tiny-desk/refs/keyboard.jpg";
export const TINY_DESK_BACKING_IMAGE =
	"https://assets.imagesart.ai/home/showcase/tiny-desk/refs/backing.jpg";

export type TinyDeskQualityId = "lite" | "standard" | "hd";

/** 按参考视频 + 输出计费：Lite 14、Standard 53、HD 176 积分。 */
export const TINY_DESK_QUALITIES: {
	id: TinyDeskQualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration: number;
}[] = [
	{ id: "lite", model: "seedance-2-mini", resolution: "480p", duration: 4 },
	{
		id: "standard",
		model: "seedance-2-mini",
		resolution: "480p",
		duration: 11,
	},
	{ id: "hd", model: "quality-2.0-ref", resolution: "720p", duration: 11 },
];

export const TINY_DESK_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as TinyDeskQualityId,
	aspectRatioOptions: ["9:16"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	durationOptions: [4, 11],
	defaultDuration: 4,
	slotCount: 3,
	requiredSlots: 1,
} as const;

export const TINY_DESK_NOTE_PREFIX = "\n\nDirector's note: ";

export function buildTinyDeskPrompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${TINY_DESK_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/** 实测固定映射重排为 UI 槽位：主唱、键盘、伴唱；H3 上限 1000，留出备注空间。 */
const BAND_PROMPT =
	"Character replacement in @Video 1. Replace ALL pixelated performers from their first visible frame, head to toe, using the exact faces, hair, skin tones, bodies and outfits in the photos. In the opening group: @Image 2 replaces the seated foreground keyboard player; @Image 3 replaces the standing back-left performer; @Image 1 replaces the standing back-right performer. Lock these identities, never blend or swap them. After the cut around 5 seconds, replace the solo singer EXCLUSIVELY with @Image 1 for the entire close-up. Never show the original red-cap man or his hoodie. Keep the original headphones and attached microphones. Copy each source performance: singing mouths, laughter, head turns, gaze, gestures and body rhythm. Keep the source framing, shot order, cut timing, studio, shelves, instruments, lighting and audio. Do not add shots or people. No blur, pixelation, text, logos or watermark.";

/** Lite 仅用开场两秒延展为四秒，不承诺后半独唱特写。 */
const BAND_LITE_PROMPT =
	"Character replacement in @Video 1, a desk-side band performance. Replace ALL pixelated performers from the first frame, head to toe, with the exact faces, hair, skin tones, bodies and outfits from the photos. @Image 1 replaces the standing back-right singer, @Image 2 the seated foreground keyboard player, and @Image 3 the standing back-left singer. Lock each identity in its position; never blend or swap faces. Keep the headphones and attached microphones. Follow their singing, smiles, head turns, hand gestures and body rhythm. Continue the same group performance naturally for 4 seconds, without a solo close-up or a new shot. Preserve the room, shelves, instruments, desk, warm lighting and framing. No original faces, extra people, blur, pixelation, text, logos or watermark.";

export const TINY_DESK_TEMPLATES: {
	id: "band";
	prompt: string;
	promptByQuality: Partial<Record<TinyDeskQualityId, string>>;
}[] = [
	{
		id: "band",
		prompt: BAND_PROMPT,
		promptByQuality: { lite: BAND_LITE_PROMPT },
	},
];
